import { ReactElement, useState } from 'react'
import {
  Alert,
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { RefreshIcon } from 'src/v3/core/icons'
import {
  restartCourse,
  selectIsRestartingByID,
  selectRestartStatusByID,
} from 'src/v3/classroom-v2/classroom-v2-wizard.slice'

// The one restart trigger, shared by the overview panel and the certificate
// surface. Both offer the same thing at the same moment — the learner has finished
// and wants another CE credit — so they ask the same question, warn about the same
// consequence and dispatch the same event. Two copies of that would be two chances
// for the wording of a destructive confirmation to drift.
//
// The only local state is whether the dialog is open, which is genuinely local:
// nothing outside this component acts on a learner considering a restart, and
// nothing needs to survive an unmount. Everything the restart itself involves —
// whether one is underway, whether the last one failed — is read from the store,
// because those are facts about the course rather than about this button.
//
// Deliberately does NOT navigate, close anything or await the dispatch. The
// restart moves the server's boundary, the middleware rehydrates, and the phase
// gate re-derives the surface from the new projection — so wherever the learner
// ends up is computed from the server's own answer. On the certificate step that
// means this component's own parent is replaced out from under it, which is why
// there's nothing here to clean up after a success.
export let RestartCourseButton = ({
  courseID,
  courseName,
  variant = 'text',
  color = 'inherit',
  size,
  fullWidth,
  label = 'Restart for New CE Credit',
}: {
  courseID: string
  courseName: string
  variant?: ButtonProps['variant']
  color?: ButtonProps['color']
  size?: ButtonProps['size']
  fullWidth?: boolean
  label?: string
}): ReactElement => {
  let dispatch = useAppDispatch()
  let isRestarting = useAppSelector(selectIsRestartingByID(courseID))
  let restartStatus = useAppSelector(selectRestartStatusByID(courseID))
  let [isConfirming, setIsConfirming] = useState(false)

  let confirm = () => {
    setIsConfirming(false)
    dispatch(restartCourse(courseID))
  }

  return (
    <>
      <Button
        variant={variant}
        color={color}
        size={size}
        fullWidth={fullWidth}
        startIcon={<RefreshIcon />}
        // Disabled for the whole restart, not just the POST: the wipe lands
        // before the refetch does, so a second press in that gap would append a
        // second boundary for a learner who only asked once.
        disabled={isRestarting}
        onClick={() => setIsConfirming(true)}
      >
        {isRestarting ? 'Restarting…' : label}
      </Button>

      {/* A failed restart is worth saying out loud precisely because nothing
          visible changed — the certificate and the completed course are still on
          screen, so silence would read as "nothing happened, press it again".
          Says only that it didn't happen: a 403 covers both "not yours" and "no
          such course" with a plain-text body, so there is nothing to classify
          and guessing between them would be inventing a diagnosis. */}
      {restartStatus === 'rejected' && !isRestarting && (
        <Alert
          severity="warning"
          role="alert"
          // Full width because this renders inside a centred flex row of buttons
          // on the certificate surface — left to size itself to its content it
          // would sit as a third narrow item alongside them.
          sx={{ mt: 2, textAlign: 'left', width: '100%' }}
        >
          We couldn&apos;t restart this course just now. Your existing
          completion and certificate are untouched — please try again in a
          moment.
        </Alert>
      )}

      {/* Restarting is not reversible from the client and it voids work the
          learner actually did, so it asks first. The dialog names the specific
          consequences rather than saying "are you sure": a passed assessment and
          a submitted survey are the things they'd be surprised to lose. */}
      <Dialog
        open={isConfirming}
        onClose={() => setIsConfirming(false)}
        aria-labelledby="restart-course-dialog-title"
        aria-describedby="restart-course-dialog-description"
      >
        <DialogTitle id="restart-course-dialog-title">
          Restart {courseName}?
        </DialogTitle>
        <DialogContent id="restart-course-dialog-description">
          You&apos;ll start this course over from the beginning. Your progress,
          your assessment and your survey all reset, and you&apos;ll need to
          complete them again to earn another certificate and CE credit.
          <br />
          <br />
          {/* The reassurance that makes the warning above safe to act on. The
              boundary only scopes future credit; the certificate they already
              hold is a record of a completion that happened, and restarting
              doesn't unmake it. */}
          The certificate you&apos;ve already earned stays in your certificates
          and your CE credit for it is unaffected.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsConfirming(false)}
            variant="text"
            color="inherit"
          >
            Cancel
          </Button>
          <Button onClick={confirm} variant="contained" color="primary">
            Restart Course
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
